//React Core Hooks
import { useEffect, useState, useContext } from "react";

// Router
import { useNavigate, useLocation as useRouterLocation } from "react-router-dom";
import { Routes, Route } from "react-router-dom";
import { Navigate } from "react-router-dom";

// Contexts / Auth
import { useAuth } from "../../context/AuthContext";
import { apiFetch } from "../../context/AuthContext";
import { useMessenger } from "../../context/MessengerContext";
import { ThemeContext } from "../../constant/theme";
import { LanguageContext } from "../../constant/LanguageContext";
import { useModal } from "../../constant/ModalContext.jsx";
import { SidebarProvider } from "../(All) Sidebar/SidebarContext.js";
import { useLogout } from "../../hooks/useLogout";
import { useProfile } from "../../hooks/useProfile";

// Assets
import s2Image from "../../Images/photos/s4.png";
import s7Image from "../../Images/photos/s7.png";

// UI Components
//import MiniChatBar from "../MiniChatBar";
import SidebarWithTopbar from "../(All) Sidebar/SidebarWithTopbar";
import ViewTypeSwitcher from "../../components/ViewTypeSwitcher";

// Helpers
import {
  getEnglishInitials,
  getAvatarColor as getStandardAvatarColor,
} from "../../components/UserAvatar";

// Split files
import translations from "./constants/translations";
import { allowedArea, getDistance } from "./config/allowedArea";
import pageToModuleMap from "./config/pageToModuleMap";
import { buildAllNavItems } from "./config/navItems";
import { useEmployeeStats } from "./hooks/useEmployeeStats";
import { useUnreadMessages } from "./hooks/useUnreadMessages";
import { useModulePermissions } from "./hooks/useModulePermissions";
import { useUserLocation } from "./hooks/useUserLocation";
import LocationBlockedScreen from "./components/LocationBlockedScreen";
import {
  renderDashboard,
  renderEmployees,
  renderPayroll,
  renderAttendance,
  renderAnalytics,
  renderReports,
  renderMessages,
  renderEscalationGroupMapping,
  renderCalendar,
  renderSettings,
  renderMo3tiAdminDocumentRequests,
  renderMo3tiAdvancementManagement,
  renderAdminLeavePage,
  renderChangesPage,
  renderConfiguration,
  renderAdminLevels,
  renderDepartments,
  renderTaxCalculator,
  renderOtherPage,
} from "./renderers/pageRenderers";

// Pages (kept for Routes)
import MessengerPage from "../(All) messenger/MessengerPage.jsx";

// Route-level permission enforcement (3-tier admin-level system)
import PageGuard from "../../components/PageGuard.jsx";

// Icons
import {
  User,
  LogOut,
  Bell,
  Plug,
  MapPinOff,
  Network,
  Settings,
  BarChart3,
  CalendarCheck,
  Users,
  CalculatorIcon,
  Calendar as CalendarIcon,
  FileText,
  Home,
  CreditCard,
  Sun,
  Moon,
  Calculator,
  GitPullRequestIcon,
  BookCheckIcon,
  DollarSignIcon,
  Coffee,
  LockIcon,
  MessageSquare,
  GitPullRequest,
  Shield,
  GitBranch,
} from "lucide-react";

function ProfessionalDashboard() {
  // Theme Context
  const { theme, setTheme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const { profile } = useProfile();

  const t = translations[language];

  const BASE_URL = process.env.REACT_APP_BASE_URL;

  // âœ… Avatar helper functions - using standardized English-only initials
  const getAvatarColor = (name) => {
    return getStandardAvatarColor(name);
  };

  // Uses shared getEnglishInitials - NEVER displays Arabic initials
  const getInitials = (name, englishName = null) => {
    return getEnglishInitials(name, englishName, "US");
  };

  const formatMessageTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const {
    conversations,
    messages,
    onlineUsers,
    currentUser,
    employeeProfilePics,
  } = useMessenger();

  const isRTL = language === "ar";

  // â”€â”€ Hooks (extracted) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const { userLocation, locationError } = useUserLocation();
  const { employeeCount, totalCompanies, counts, payrollTotal } = useEmployeeStats(BASE_URL);
  const { modulePermissions, loadingPermissions } = useModulePermissions(BASE_URL, profile);

  const {
    conversations: messengerConversations,
    socket,
    isConnected,
  } = useMessenger();

  const { unreadMessages, setUnreadMessages, hasNewMessage, setHasNewMessage } = useUnreadMessages(
    BASE_URL,
    socket,
    isConnected,
    messengerConversations,
  );
  // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  // User info from /users/me (profile), fall back to localStorage
  const user = {
    username: profile?.employeeName || localStorage.getItem("emoloyeeName"),
    email: profile?.employeeEmail || localStorage.getItem("email"),
    role: profile?.role || localStorage.getItem("role"),
  };

  const [notifications, setNotifications] = useState(3);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [currentPage, setCurrentPage] = useState(() => {
    // Ø§Ø³ØªØ±Ø¬Ø§Ø¹ Ø§Ù„ØµÙØ­Ø© Ø§Ù„Ù…Ø­ÙÙˆØ¸Ø© Ø¹Ù†Ø¯ Ø§Ù„ØªØ­Ù…ÙŠÙ„
    const savedPage = localStorage.getItem("adminCurrentPage");
    return savedPage || "dashboard";
  });

  const [viewType, setViewType] = useState(() => {
    return localStorage.getItem("viewType") || "admin";
  });

  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
  const { hasFeatureAccess, getPageState } = useAuth();
  const { logout } = useLogout();
  const { showModal } = useModal();

  // "Communicator Module (PTT & Private Messages)" is the umbrella toggle a
  // superadmin actually reaches for in Communication Modules — it wasn't
  // wired to anything, so flipping it left the Messenger page locked unless
  // the separate, easy-to-miss "Private Messaging" sub-toggle was ALSO set.
  const isPrivateMessagingEnabled =
    (modulePermissions?.privateMessaging || modulePermissions?.communicatorModule) ?? false;

  // Get admin level from profile (/users/me) or localStorage
  const hasAdminLevel = () => {
    if (profile?.adminLevel) {
      const al = profile.adminLevel;
      return Boolean(al.adminLevelId || (Array.isArray(al.modules) && al.modules.length > 0));
    }
    const adminLevel = localStorage.getItem("adminLevel");
    if (!adminLevel) return false;
    try {
      const parsed = JSON.parse(adminLevel);
      return (
        parsed?.adminLevelId || (parsed?.modules && parsed.modules.length > 0)
      );
    } catch (e) {
      return false;
    }
  };

  // Handle view type change with navigation
  const handleViewTypeChange = (type) => {
    setViewType(type);
    localStorage.setItem("viewType", type);
    if (type === "user") {
      // If user is admin (with or without adminLevel), go to userprofileadmin
      // Otherwise check if has admin level, go to userprofileadmin, else go to userprofilemanager
      if (user.role === "admin" || hasAdminLevel()) {
        navigate("/userprofileadmin/professionalProfile");
      } else {
        navigate("/userprofilemanager/professionalProfile");
      }
    } else if (type === "manager") {
      navigate("/DashboardManager/dashboard");
    }
    if (type === "admin") {
      // "/dashboard" alone lands the shell with no page segment selected;
      // every other "go to admin dashboard" call in the app (handlePageChange,
      // useUserProfileState's own handleViewTypeChange) targets the actual
      // "dashboard" page underneath the shell — match that here too.
      navigate("/dashboard/dashboard");
    }
  };

  // Toggle between light and dark theme
  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
  };

  useEffect(() => {
    // Ø­ÙØ¸ Ø§Ù„ØµÙØ­Ø© Ø§Ù„Ø­Ø§Ù„ÙŠØ© ÙÙŠ localStorage Ø¹Ù†Ø¯ ØªØºÙŠÙŠØ±Ù‡Ø§
    localStorage.setItem("adminCurrentPage", currentPage);
  }, [currentPage]);


  // Logout function
  const handleLogout = async () => {
    try {
      localStorage.removeItem("adminCurrentPage");
      localStorage.removeItem("nav_admin");
      localStorage.removeItem("nav_manager");
      localStorage.removeItem("nav_employee");
    } catch (e) {}

    await logout(); // â† hook handles backend revocation + session clear + navigate
  };

  const handleSwitchEmployee = () => {
    try {
      // Clear the saved page when switching
      localStorage.removeItem("adminCurrentPage");
      // Reset to dashboard
      setCurrentPage("dashboard");
      navigate("/userprofileadmin/professionalProfile");
    } catch (error) {
      console.error("Switch error:", error);
      navigate("/dashboard");
    }
  };

  useEffect(() => {
    if (currentPage === "messages") {
      setHasNewMessage(false);
    }
  }, [currentPage, setHasNewMessage]);

  // Build nav items using extracted builder
  const allNavItems = buildAllNavItems(t, isPrivateMessagingEnabled, unreadMessages, hasNewMessage, currentPage);

  useEffect(() => {
    // Sync currentPage with URL path
    const pathParts = routerLocation.pathname.split('/').filter(Boolean);
    const lastPart = pathParts[pathParts.length - 1];

    if (lastPart) {
      const matchedNavItem = allNavItems.find(
        (item) => item.page.toLowerCase() === lastPart.toLowerCase()
      );

      if (matchedNavItem) {
        if (currentPage !== matchedNavItem.page) {
          setCurrentPage(matchedNavItem.page);
        }
      } else {
        // Fallback to dashboard if path is unhandled or root
        if (currentPage !== "dashboard") {
          setCurrentPage("dashboard");
        }
      }
    } else {
      if (currentPage !== "dashboard") {
        setCurrentPage("dashboard");
      }
    }
  }, [routerLocation.pathname, allNavItems, currentPage]);

  // Helper: Check if user has any actionable changes features
  const hasAnyChangesPageActionFeatures = () => {
    const actionFeatures = [
      "promoteemployee",
      "demoteemployee",
      "transferemployee",
      "salaryadjustment",
      "terminateemployment",
      "suspendemployee",
      "rehireemployee",
      "contractupdate",
      "jobrotation",
      "performancereview",
      "trainingassignment",
      "approveactions",
      "rejectactions",
      "assignmanagers",
      "bulkactions",
      "export",
    ];
    return actionFeatures.some((feature) =>
      hasFeatureAccess("changesPage", feature)
    );
  };

  // Change current page
  const handlePageChange = (page) => {
    const navItem = allNavItems.find((item) => item.page === page);
    if (navItem?.locked) {
      const title = (t && t.notice) || "Notice";
      const message =
        navItem.lockMessage || (t && t.featureNotAvailable) || undefined;
      showModal({
        title,
        message,
        options: {
          requestAccessEndpoint: process.env.REACT_APP_BASE_URL
            ? `${process.env.REACT_APP_BASE_URL}/api/request-module-access`
            : null,
          module: navItem.page,
        },
      });
      return;
    }
    setCurrentPage(page);
    navigate(`/dashboard/${page}`);
  };


  // Filter navigation items based on user's admin level permissions.
  // Three-state (v2): HIDDEN removes the item, VISIBLE_ACCESS_DENIED keeps it
  // visible with a lock suffix (still clickable), VISIBLE_ACCESSIBLE is normal.
  const navItems = allNavItems
    .filter((item) => {
      // Dashboard is always visible
      if (item.page === "dashboard") return true;

      // Super admins see everything
      if (user.role === "superadmin") return true;

      // Regular admins see everything except pages that are explicitly locked
      if (user.role === "admin") {
        // Allow admins to see all pages including configuration
        return true;
      }

      // âœ… Special handling for Changes Page - hide if no action features enabled
      if (item.page === "ChangesPage") {
        const state = getPageState("changesPage");
        if (state === "VISIBLE_ACCESSIBLE" && !hasAnyChangesPageActionFeatures()) {
          console.log(
            `[Dashboard] Changes Page hidden - module enabled but no action features available`
          );
          return false;
        }
      }

      // Get the required module for this page
      const requiredModule = pageToModuleMap[item.page];

      // If no module mapping exists, show the item (backward compatibility)
      if (!requiredModule) {
        console.log(
          `[Dashboard] No module mapping for page: ${item.page} - showing by default`,
        );
        return true;
      }

      // Show unless the page is HIDDEN for this user
      return getPageState(requiredModule) !== "HIDDEN";
    })
    .map((item) => {
      if (user.role === "superadmin" || user.role === "admin") return item;
      const requiredModule = pageToModuleMap[item.page];
      if (!requiredModule) return item;
      return getPageState(requiredModule) === "VISIBLE_ACCESS_DENIED"
        ? { ...item, accessDenied: true }
        : item;
    });

  // Render current page based on navigation
  const renderCurrentPage = () => {
    switch (currentPage) {
      case "dashboard":
        return renderDashboard();
      case "employees":
        return renderEmployees();
      case "payroll":
        return renderPayroll();
      case "analytics":
        return renderAnalytics();
      case "attendance":
        return renderAttendance();
      case "reports":
        return renderReports();
      // case "messages": // â¬…ï¸ Ø£Ø¶Ù Ù‡Ø°Ø§
      //   return renderMessages();
      case "calendar":
        return renderCalendar();
      case "configuration":
        return renderConfiguration();
      case "messages": {
        // If no location yet
        if (!userLocation && !locationError) {
          return <div>ðŸ“ Please allow location access...</div>;
        }

        if (locationError) {
          return (
            <div style={{ color: "red", padding: "20px" }}>{locationError}</div>
          );
        }

        // Calculate distance
        const distance = getDistance(
          userLocation.lat,
          userLocation.lng,
          allowedArea.lat,
          allowedArea.lng,
        );

        if (distance > allowedArea.radius) {
          return (
            <LocationBlockedScreen
              t={t}
              distance={distance}
              pageName={t.messages}
            />
          );
        }
        // âœ… Allowed â†’ show connectors page
        return renderMessages();
      }
      case "Settings":
        return renderSettings();
      case "Mo3tiAdminDocumentRequests":
        return renderMo3tiAdminDocumentRequests();
      case "Mo3tiAdvancementManagement":
        return renderMo3tiAdvancementManagement();
      case "TaxCalculator":
        return renderTaxCalculator();
      case "LeaveApprovalPage":
        return renderAdminLeavePage();
      case "ChangesPage":
        return renderChangesPage();
      case "adminLevels":
        return renderAdminLevels();
      case "departments":
        return renderDepartments();

      default:
        return renderDashboard();
    }
  };

  // Wrap a route element in PageGuard using this shell's pageToModuleMap.
  // Hidden pages redirect to /dashboard/dashboard (the app's own convention
  // for "go to admin dashboard" navigations).
  const guardPage = (page, label, element) => (
    <PageGuard
      pageKey={pageToModuleMap[page] || null}
      pageLabel={label}
      redirectTo="/dashboard/dashboard"
    >
      {element}
    </PageGuard>
  );

  if (loadingPermissions) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: theme === "dark" ? "rgba(15, 23, 42, 1)" : "#f1f1f1",
        }}
      >
        <div
          style={{
            color: theme === "dark" ? "white" : "#0f172a",
            fontSize: "18px",
          }}
        >
          {t.loadingPermissions}
        </div>
      </div>
    );
  }

  // Main render
  return (
    <div
      style={{
        minHeight: "100vh",
        fontFamily: "Segoe UI, Roboto, sans-serif",
        position: "relative",
      }}
    >
      {/* Background image */}

      <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        <div
          style={{
            position: "absolute",
            top: "-140px",
            right: "-120px",
            width: "300px",
            height: "300px",
            borderRadius: "50%",
            filter: "blur(60px)",
            background:
              theme === "light"
                ? "rgba(59,130,246,0.05)"
                : "rgba(99,102,241,0.1)",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "-140px",
            left: "-140px",
            width: "320px",
            height: "320px",
            borderRadius: "50%",
            filter: "blur(60px)",
            background:
              theme === "light"
                ? "rgba(139,92,246,0.05)"
                : "rgba(59,130,246,0.1)",
          }}
        />
      </div>

      {/* Sidebar with Topbar */}
      <SidebarWithTopbar
        navItems={navItems}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
        user={user}
        notifications={notifications}
        handleLogout={handleLogout}
        handleSwitchEmployee={handleSwitchEmployee}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        themeToggle={
          <button
            style={{
              padding: "8px",
              borderRadius: "12px",
              cursor: "pointer",
              color: theme === "light" ? "#0f172a" : "white",
              background:
                theme === "light" ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)",
            }}
            onClick={toggleTheme}
          >
            {theme === "light" ? <Moon size={20} /> : <Sun size={20} />}
          </button>
        }
        viewModeSwitcher={
          <ViewTypeSwitcher
            viewType={viewType}
            onSelect={(type) => handleViewTypeChange(type)}
            hasAdminLevel={() => hasAdminLevel()}
            role={user.role}
          />
        }
      >
        {/* âŒ BEFORE - Ø´ÙŠÙ„ Ø§Ù„Ø³Ø·Ø± Ø¯Ù‡ */}
        {/* <div style={{ padding: "32px 24px" }}>{renderCurrentPage()}</div> */}

        {/* âœ… AFTER - Ø§Ø³ØªØ¨Ø¯Ù„Ù‡ Ø¨Ø¯Ù‡ */}
        <div
          style={{
            padding: "0",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            //overflow: "hidden", // ✅ أضف هذا
            marginTop: currentPage === "messages" ? 0 : 40,
          }}
        >
          <Routes>
            <Route path="/dashboard" element={renderDashboard()} />
            <Route
              path="/employees"
              element={guardPage("employees", t.employees || "Employees", renderEmployees())}
            />
            <Route
              path="/payroll"
              element={guardPage("payroll", t.payroll || "Payroll", renderPayroll())}
            />
            <Route
              path="/attendance"
              element={guardPage("attendance", t.attendance || "Attendance", renderAttendance())}
            />
            <Route path="/analytics" element={renderAnalytics()} />
            <Route
              path="/reports"
              element={guardPage("reports", t.reports || "Reports", renderReports())}
            />
            <Route
              path="/calendar"
              element={guardPage("calendar", t.calendar || "Calendar", renderCalendar())}
            />
            {/* <Route path="/messages" element={renderMessages()} /> */}
            <Route
              path="/configuration"
              element={guardPage("configuration", t.configuration || "Configuration", renderConfiguration())}
            />
            <Route
              path="/adminLevels"
              element={guardPage("adminLevels", t.adminLevels || "Admin Levels", renderAdminLevels())}
            />
            <Route
              path="/departments"
              element={guardPage("departments", t.departments || "Departments", renderDepartments())}
            />

            {/* <Route
  path="/connectors"
  element={
    isPrivateMessagingEnabled ? (
      <Connectors />
    ) : (
      <Navigate to="/dashboard" replace />
    )
  }
/> */}
            <Route
              path="/messages"
              element={guardPage(
                "messages",
                t.messages || "Messages",
                isPrivateMessagingEnabled ? (
                  <MessengerPage /> // âœ… ØµØ­: Ø­Ø±Ù ÙƒØ¨ÙŠØ±
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              )}
            />
            <Route
              path="/EscalationGroupMapping"
              element={guardPage(
                "EscalationGroupMapping",
                t.escalationMapping || "Escalation Mapping",
                renderEscalationGroupMapping()
              )}
            />
            <Route
              path="/Mo3tiAdminDocumentRequests"
              element={guardPage(
                "Mo3tiAdminDocumentRequests",
                t.documents || "Documents",
                renderMo3tiAdminDocumentRequests()
              )}
            />
            <Route
              path="/Mo3tiAdvancementManagement"
              element={guardPage(
                "Mo3tiAdvancementManagement",
                t.solfa || "Solfa",
                renderMo3tiAdvancementManagement()
              )}
            />
            <Route
              path="/TaxCalculator"
              element={guardPage("TaxCalculator", t.taxCalculator || "Tax Calculator", renderTaxCalculator())}
            />
            <Route
              path="/LeaveApprovalPage"
              element={guardPage("LeaveApprovalPage", t.leave || "Leave", renderAdminLeavePage())}
            />
            <Route
              path="/ChangesPage"
              element={guardPage("ChangesPage", t.employeeChanges || "Employee Changes", renderChangesPage())}
            />
            <Route
              path="/Settings"
              element={guardPage("Settings", t.settings || "Settings", renderSettings())}
            />
          </Routes>
        </div>
      </SidebarWithTopbar>

      {/* â„ï¸ Snow Animation - ØªØ£Ø«ÙŠØ± Ø§Ù„Ø«Ù„Ø¬ */}
      {/* <div className="snowfall">
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="snowflake"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 10}s`,
              animationDuration: `${5 + Math.random() * 10}s`,
              opacity: Math.random() * 0.6 + 0.4,
              fontSize: `${12 + Math.random() * 12}px`
            }}
          >
            {['â„', 'â…', 'â†', 'âœ»', 'âœ¼', 'â‰'][Math.floor(Math.random() * 6)]}
          </div>
        ))}
      </div> */}
      {/* â„ï¸ Snow Animation - ØªØ£Ø«ÙŠØ± Ø§Ù„Ø«Ù„Ø¬ */}
      {/* ... ÙƒÙˆØ¯ Ø§Ù„Ø«Ù„Ø¬ Ø§Ù„Ù…ÙˆØ¬ÙˆØ¯ ... */}

      {/* âœ… Ø£Ø¶Ù Ù‡Ù†Ø§ Mini Chat Bar */}
      {/* âœ… Mini Chat Bar - ÙÙŠ Ø¢Ø®Ø± Ø§Ù„Ù€ return Ù‚Ø¨Ù„ Ø¥ØºÙ„Ø§Ù‚ Ø§Ù„Ù€ div */}
      {/* âœ… Mini Chat Bar - Ù‚Ø¨Ù„ Ø¥ØºÙ„Ø§Ù‚ Ø¢Ø®Ø± </div> */}
      {/* <MiniChatBar
  conversations={conversations || []}
  onlineUsers={onlineUsers || new Set()}
  messages={messages || {}}
  currentUser={currentUser}
  socket={socket}
  isConnected={isConnected}
  onSendMessage={(chatId, message) => {
    if (!socket || !isConnected) return;
    
    socket.emit('private_message:send', {
      receiverId: chatId,
      content: message,
      type: 'text',
    });
  }}
  onOpenFullChat={() => handlePageChange('messages')}
  employeeProfilePics={employeeProfilePics || {}}
  getAvatarColor={getAvatarColor}
  getInitials={getInitials}
  formatMessageTime={formatMessageTime}
  renderMessageContent={(message) => message?.text || ''}
/> */}

      {/* Styles */}
      <style>
        {`
          /* Snow Animation Styles - Ø£Ù†Ù…Ø§Ø· ØªØ£Ø«ÙŠØ± Ø§Ù„Ø«Ù„Ø¬ */
          .snowfall {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            z-index: 9999;
            overflow: hidden;
          }

          .snowflake {
            position: absolute;
            top: -20px;
            color: #fff;
            text-shadow: 
              0 0 10px rgba(255, 255, 255, 1),
              0 0 20px rgba(200, 230, 255, 0.8),
              0 0 30px rgba(150, 200, 255, 0.6);
            animation: fall linear infinite;
            user-select: none;
            filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.9));
          }

          @keyframes fall {
            0% {
              transform: translateY(0) translateX(0) rotate(0deg);
              opacity: 1;
            }
            25% {
              transform: translateY(25vh) translateX(20px) rotate(90deg);
              opacity: 0.8;
            }
            50% {
              transform: translateY(50vh) translateX(-20px) rotate(180deg);
              opacity: 0.6;
            }
            75% {
              transform: translateY(75vh) translateX(20px) rotate(270deg);
              opacity: 0.8;
            }
            100% {
              transform: translateY(100vh) translateX(0) rotate(360deg);
              opacity: 0;
            }
          }

          /* Responsive Ù„Ù„Ù…ÙˆØ¨Ø§ÙŠÙ„ */
          @media (max-width: 768px) {
            .snowflake {
              font-size: 12px !important;
            }
          }

          @media (max-width: 480px) {
            .snowflake {
              font-size: 10px !important;
            }
          }
        `}
      </style>
    </div>
  );
}

export default ProfessionalDashboard;